import React, { createContext, useContext, useEffect, useState } from "react";
import { BASE_URL } from "../config/apiConfig";
import { toast } from "react-toastify";

const LeavesContext = createContext();
export const useLeaves = () => useContext(LeavesContext);

export const LeavesProvider = ({ children }) => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getToken = () => localStorage.getItem("token");

  // Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // Fetch all leaves
  const fetchLeaves = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/leaves`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (res.status === 401) {
        setError("Session expired. Please log in again.");
        return;
      }

      if (!res.ok) {
        setError("Failed to fetch leaves");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setLeaves(data.data || []);
      } else {
        setError(data.message || "Failed to fetch leaves");
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching leaves");
    } finally {
      setLoading(false);
    }
  };

  // Fetch single leave by ID
  const fetchLeaveById = async (id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/leaves/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) return null;

      return await parseJson(res);
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  // Add new leave
  const addLeave = async (newData) => {
    try {
      const token = getToken();
      const user = JSON.parse(localStorage.getItem("user"));

      const updatedData = {
        ...newData,
        user_id: user?.id,
        create_audit_id: user?.id,
      };

      const res = await fetch(`${BASE_URL}/leaves`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedData),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        console.error("Add leave failed:", res.status, data);

        return {
          success: false,
          message: data?.message || "Failed to add leave",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 201 || data.status === 200) {
        await fetchLeaves();

        toast.success(data.message || "Leave added successfully!");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to add leave",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error adding leave",
      };
    }
  };

  // Update leave
  const updateLeave = async (id, updatedData) => {
    if (!id) {
      console.error("updateLeave called without an id", updatedData);
      return {
        success: false,
        message: "Missing leave ID — cannot update.",
      };
    }

    try {
      const token = getToken();

      if (!token) {
        return {
          success: false,
          message: "Session expired. Please log in again.",
        };
      }

      const res = await fetch(`${BASE_URL}/leaves/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedData),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        console.error("Update leave failed:", res.status, JSON.stringify(data, null, 2));

        // try to surface field-specific validation messages if present
        let detail = data?.message;
        if (!detail && data?.messages) {
          detail =
            typeof data.messages === "string"
              ? data.messages
              : Object.entries(data.messages)
                  .map(([field, msg]) => `${field}: ${msg}`)
                  .join(", ");
        }

        return {
          success: false,
          message: detail || `Failed to update leave (status ${res.status})`,
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchLeaves();

        toast.success(data.message || "Leave updated successfully!");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update leave",
      };
    } catch (err) {
      console.error("Update leave error:", err);

      return {
        success: false,
        message: "Error updating leave",
      };
    }
  };

  // Manager Approve / Reject Leave
  const managerApproval = async (id, status, remarks = "") => {
    try {
      const token = getToken();
      const user = JSON.parse(localStorage.getItem("user"));

      const res = await fetch(`${BASE_URL}/leaves/manager-approval/${id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status,
          remarks,
          update_audit_id: user?.id,
        }),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        console.error("Manager approval failed:", res.status, data);

        return {
          success: false,
          message: data?.message || "Failed to update manager approval",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchLeaves();

        toast.success(data.message || "Leave status updated!");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update manager approval",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error updating manager approval",
      };
    }
  };

  // Update leave status
  const updateLeaveStatus = async (id, status, remarks) => {
    try {
      const token = getToken();
      const user = JSON.parse(localStorage.getItem("user"));

      const res = await fetch(`${BASE_URL}/leaves/${id}/status`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status,
          remarks,
          update_audit_id: user?.id,
        }),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        console.error("Update leave status failed:", res.status, data);

        return {
          success: false,
          message: data?.message || "Failed to update leave status",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchLeaves();

        toast.success(data.message || "Leave status updated!");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update leave status",
      };
    } catch (err) {
      console.error(err);
      setError("Error updating leave status");

      return {
        success: false,
        message: "Error updating leave status",
      };
    }
  };

  // Delete leave
  const deleteLeave = async (id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/leaves/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to delete leave",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchLeaves();

        toast.success(data.message || "Leave deleted successfully!");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to delete leave",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error deleting leave",
      };
    }
  };

  useEffect(() => {
    const token = getToken();
    if (token) fetchLeaves();
  }, []);

  return (
    <LeavesContext.Provider
      value={{
        leaves,
        loading,
        error,
        fetchLeaves,
        fetchLeaveById,
        addLeave,
        setLeaves,
        updateLeave,
        updateLeaveStatus,
        managerApproval,
        deleteLeave,
      }}
    >
      {children}
    </LeavesContext.Provider>
  );
};